import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { AskRequest, QueueView } from '@hone-automation/common';
import { format } from 'date-fns';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';
import { MobileSlideUpPanel } from '../MobileSlideUpPanel';
import { SimpleDatePicker } from '../../Panels/ConversationPanel/components/Header/components/DueDateButton/components/SimpleDatePicker';
import { RecategorizeDialog } from '../../Panels/ConversationPanel/components/Header/components/ActionsDropdown/RecategorizeDialog';
import { ResolveDialog } from '../../Panels/ConversationPanel/components/Header/components/ActionsDropdown/ResolveDialog';
import { usePatchQueueRequestMutation, useRecategorizeQueueRequestMutation } from 'hooks/queue/';
import { useQueueContext } from '../../../Queue/QueueContext';
import { useAuthContext } from 'context/useAuthContext';
import { parseLocalDate } from '../../Panels/BrowserPanel/utils/dateGrouping';
import {
  IconReassign,
  IconValidateNew,
  IconNavCalendar,
  IconChevron,
  IconKaiSynth,
  IconFolderAdd,
  IconFolderDelete,
  IconFolderMove,
} from 'components/Icons';
import SummarizePanel from 'presentation/components/Queue/panels/SummarizePanel/SummarizePanel';
import { getRequestActionVisibility } from '../../utils/actionVisibility';
import './MobileActionsPanel.scss';

export interface MobileActionsPanelProps {
  /** The request to act on */
  request: AskRequest;
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback when panel closes */
  onClose: () => void;
  /** Whether user can manage queue (accountant) */
  canManageQueue?: boolean;
  /** Whether resolving should show the comment dialog */
  allowResolveDialog?: boolean;
  /** Whether to show the resolve action in this panel */
  showResolveAction?: boolean;
  /** Number of available groups */
  groupCount?: number;
  /** Whether request is in a group (has parentId) */
  hasParentGroup?: boolean;
  /** Whether other groups exist (for "move to group") */
  hasOtherGroups?: boolean;
  /** Whether selected item is a group container */
  isGroupContainer?: boolean;
  /** Callback to open group picker in assign mode */
  onAddToGroup?: () => void;
  /** Callback to remove from current group */
  onRemoveFromGroup?: () => void;
  /** Callback to open group picker for moving to another group */
  onMoveToGroup?: () => void;
  /** Callback to dissolve a group */
  onDissolveGroup?: () => void;
  /** Callback to create a new group with this ticket */
  onCreateGroup?: () => void;
}

/**
 * MobileActionsPanel - Slide-up actions panel for mobile
 *
 * A mobile-native action sheet pattern using MobileSlideUpPanel.
 * Provides the same actions as MobileActionsDropdown but with
 * better mobile UX (bottom sheet, larger touch targets, swipe to dismiss).
 *
 * Actions:
 * - Re-categorize (if allowed)
 * - Resolve (if not closed)
 * - Due Date with inline date picker
 */
export const MobileActionsPanel: React.FC<MobileActionsPanelProps> = ({
  request,
  isOpen,
  onClose,
  canManageQueue = false,
  allowResolveDialog = false,
  showResolveAction = true,
  groupCount = 0,
  hasParentGroup = false,
  hasOtherGroups = false,
  isGroupContainer = false,
  onAddToGroup,
  onRemoveFromGroup,
  onMoveToGroup,
  onDissolveGroup,
  onCreateGroup,
}) => {
  const prefix = prefixClass('ksq2-mobile-actions-panel');
  const { isBookkeeperAdmin } = useAuthContext();
  const { dispatch, state } = useQueueContext();
  const { activeView } = state;

  // Dialog states
  const [isRecategorizeOpen, setIsRecategorizeOpen] = useState(false);
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [isDueDateExpanded, setIsDueDateExpanded] = useState(false);
  const [isSummarizeOpen, setIsSummarizeOpen] = useState(false);

  // Due date state
  const initialDate = request.dueDate ? parseLocalDate(request.dueDate) : null;
  const [tempDueDate, setTempDueDate] = useState<Date | null>(initialDate);

  // Sync due date with request changes
  useEffect(() => {
    const newDate = request.dueDate ? parseLocalDate(request.dueDate) : null;
    setTempDueDate(newDate);
  }, [request.dueDate]);

  // Reset expanded state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setIsDueDateExpanded(false);
    }
  }, [isOpen]);

  /**
   * Highlight a filter with animation
   */
  const highlightFilter = useCallback(
    (targetFilter: QueueView) => {
      dispatch({ type: 'HIGHLIGHT_FILTER', payload: targetFilter });
      setTimeout(() => {
        dispatch({ type: 'HIGHLIGHT_FILTER', payload: null });
      }, 500);
    },
    [dispatch]
  );

  // Resolve mutation - query invalidation is handled by usePatchQueueRequestMutation
  const resolveMutation = usePatchQueueRequestMutation(data => {
    if (data) {
      dispatch({ type: 'SET_SELECTED_ITEM', payload: data });
      highlightFilter('Completed');
    }
  });

  // Due date mutation - query invalidation is handled by usePatchQueueRequestMutation
  const dueDateMutation = usePatchQueueRequestMutation(data => {
    if (data) {
      dispatch({ type: 'SET_SELECTED_ITEM', payload: data });
    }
  });

  // Recategorize mutation
  const recategorizeMutation = useRecategorizeQueueRequestMutation(request.locationId, request.status, (_, trx) => {
    const statusChanged = trx.status !== request.status;

    dispatch({
      type: 'UPDATE_REQUEST',
      payload: {
        request: trx,
        status: trx.status,
      },
    });

    dispatch({ type: 'SET_SELECTED_ITEM', payload: trx });
    if (statusChanged) {
      const targetFilter: QueueView =
        trx.status === 'Closed' ? 'Completed' : trx.status === 'In Progress' ? 'Client' : 'Me';
      highlightFilter(targetFilter);
    }

    setIsRecategorizeOpen(false);
  });

  // Handlers
  const handleResolve = useCallback(() => {
    if (allowResolveDialog) {
      setIsResolveOpen(true);
      onClose();
      return;
    }
    resolveMutation.mutate({ id: request.id, status: 'Closed' });
    onClose();
  }, [allowResolveDialog, resolveMutation, request.id, onClose]);

  const handleResolveSubmit = useCallback(
    ({ comment }: { comment: string }) => {
      resolveMutation.mutate({ id: request.id, status: 'Closed', comment });
      setIsResolveOpen(false);
    },
    [resolveMutation, request.id]
  );

  const handleRecategorize = useCallback(() => {
    setIsRecategorizeOpen(true);
    onClose();
  }, [onClose]);

  const handleRecategorizeSubmit = useCallback(
    ({ comment, account }: { comment: string; account: string }) => {
      recategorizeMutation.mutate({ trxId: request.id, newAccountId: account, comment });
    },
    [recategorizeMutation, request.id]
  );

  const handleDueDateToggle = useCallback(() => {
    setIsDueDateExpanded(prev => !prev);
  }, []);

  const handleDueDateChange = useCallback((date: Date | null) => {
    setTempDueDate(date);
  }, []);

  const handleDueDateConfirm = useCallback(() => {
    const dueDateString = tempDueDate ? format(tempDueDate, 'yyyy-MM-dd') : null;
    dueDateMutation.mutate({ id: request.id, dueDate: dueDateString });
    setIsDueDateExpanded(false);
    onClose();
  }, [tempDueDate, dueDateMutation, request.id, onClose]);

  const handleDueDateClear = useCallback(() => {
    setTempDueDate(null);
    dueDateMutation.mutate({ id: request.id, dueDate: null });
    setIsDueDateExpanded(false);
    onClose();
  }, [dueDateMutation, request.id, onClose]);

  const handleAiSummary = useCallback(() => {
    setIsSummarizeOpen(true);
    onClose();
  }, [onClose]);

  // Determine available actions
  const { isClosed, showResolve, showRecategorize, showDueDate } = getRequestActionVisibility(request, {
    canManageQueue,
    isBookkeeperAdmin,
  });

  // Format current due date for display
  const dueDateLabel = useMemo(() => {
    if (request.dueDate) {
      const date = parseLocalDate(request.dueDate);
      return format(date, 'MMM d, yyyy');
    }
    return 'Set due date';
  }, [request.dueDate]);

  return (
    <>
      <MobileSlideUpPanel
        isOpen={isOpen}
        onClose={onClose}
        title="Actions"
        showExplicitCloseButton={false}
        initialHeight={250}
        useFitContent
      >
        <div className={prefix('__content')}>
          {/* Re-categorize */}
          {showRecategorize && (
            <button
              type="button"
              className={prefix('__item')}
              onClick={handleRecategorize}
              disabled={recategorizeMutation.isPending}
            >
              <span className={prefix('__item-icon')}>
                <IconReassign />
              </span>
              <span className={prefix('__item-label')}>Re-categorize</span>
            </button>
          )}

          {/* Resolve */}
          {showResolve && showResolveAction && (
            <button
              type="button"
              className={prefix('__item')}
              onClick={handleResolve}
              disabled={resolveMutation.isPending}
            >
              <span className={prefix('__item-icon')}>
                <IconValidateNew />
              </span>
              <span className={prefix('__item-label')}>Resolve</span>
            </button>
          )}

          {/* AI Summary */}
          <button type="button" className={prefix('__item')} onClick={handleAiSummary}>
            <span className={prefix('__item-icon')}>
              <IconKaiSynth />
            </span>
            <span className={prefix('__item-label')}>AI Summary</span>
            <span className={prefix('__item-badge')}>BETA</span>
          </button>

          {/* Due Date */}
          {showDueDate && (
            <>
              <button
                type="button"
                className={classNames(prefix('__item'), prefix('__item--expandable'), {
                  [prefix('__item--expanded')]: isDueDateExpanded,
                })}
                onClick={handleDueDateToggle}
              >
                <span className={prefix('__item-icon')}>
                  <IconNavCalendar />
                </span>
                <span className={prefix('__item-label')}>{dueDateLabel}</span>
                <span
                  className={classNames(prefix('__item-chevron'), {
                    [prefix('__item-chevron--expanded')]: isDueDateExpanded,
                  })}
                >
                  <IconChevron />
                </span>
              </button>

              {/* Due Date Picker (expanded) */}
              {isDueDateExpanded && (
                <div className={prefix('__date-picker')}>
                  <SimpleDatePicker
                    selectedDate={tempDueDate}
                    onDateChange={handleDueDateChange}
                    onConfirm={handleDueDateConfirm}
                    onClear={handleDueDateClear}
                  />
                </div>
              )}
            </>
          )}

          {/* Group Actions */}
          {/* Create Group with this ticket - only when not in a group */}
          {!isGroupContainer && !hasParentGroup && onCreateGroup && (
            <button
              type="button"
              className={classNames(prefix('__item'), prefix('__item--group'))}
              onClick={() => {
                onCreateGroup();
                onClose();
              }}
            >
              <span className={prefix('__item-icon')}>
                <IconFolderAdd />
              </span>
              <span className={prefix('__item-label')}>Create Group</span>
            </button>
          )}

          {/* Add to Group - only when request has no parentId and groups exist */}
          {!isGroupContainer && !hasParentGroup && groupCount > 0 && onAddToGroup && (
            <button
              type="button"
              className={classNames(prefix('__item'), prefix('__item--group'))}
              onClick={() => {
                onAddToGroup();
                onClose();
              }}
            >
              <span className={prefix('__item-icon')}>
                <IconFolderAdd />
              </span>
              <span className={prefix('__item-label')}>Add to Group</span>
            </button>
          )}

          {/* Remove from Group - only when request is in a group */}
          {!isGroupContainer && hasParentGroup && onRemoveFromGroup && (
            <button
              type="button"
              className={classNames(prefix('__item'), prefix('__item--group'))}
              onClick={() => {
                onRemoveFromGroup();
                onClose();
              }}
            >
              <span className={prefix('__item-icon')}>
                <IconFolderDelete />
              </span>
              <span className={prefix('__item-label')}>Remove from Group</span>
            </button>
          )}

          {/* Move to Another Group - only when in a group and other groups exist */}
          {!isGroupContainer && hasParentGroup && hasOtherGroups && onMoveToGroup && (
            <button
              type="button"
              className={classNames(prefix('__item'), prefix('__item--group'))}
              onClick={() => {
                onMoveToGroup();
                onClose();
              }}
            >
              <span className={prefix('__item-icon')}>
                <IconFolderMove />
              </span>
              <span className={prefix('__item-label')}>Move to Another Group</span>
            </button>
          )}

          {/* Dissolve Group - only for group containers */}
          {isGroupContainer && onDissolveGroup && (
            <button
              type="button"
              className={classNames(prefix('__item'), prefix('__item--group'), prefix('__item--danger'))}
              onClick={() => {
                onDissolveGroup();
                onClose();
              }}
            >
              <span className={prefix('__item-icon')}>
                <IconFolderDelete />
              </span>
              <span className={prefix('__item-label')}>Dissolve Group</span>
            </button>
          )}
        </div>
      </MobileSlideUpPanel>

      {/* Recategorize Dialog */}
      <RecategorizeDialog
        isOpen={isRecategorizeOpen}
        isPending={recategorizeMutation.isPending}
        onClose={() => setIsRecategorizeOpen(false)}
        onSubmit={handleRecategorizeSubmit}
      />

      {/* Resolve Dialog */}
      <ResolveDialog
        isOpen={isResolveOpen}
        isPending={resolveMutation.isPending}
        onClose={() => setIsResolveOpen(false)}
        onSubmit={handleResolveSubmit}
      />

      {/* AI Summary Panel */}
      <MobileSlideUpPanel
        isOpen={isSummarizeOpen}
        onClose={() => setIsSummarizeOpen(false)}
        title="AI Summary"
        titleIcon={<IconKaiSynth />}
        showBetaBadge
        fullSizeOnOpen
      >
        <SummarizePanel request={request} className="mobile-summarize-panel" />
      </MobileSlideUpPanel>
    </>
  );
};

export default MobileActionsPanel;
